import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../lib/auth/useAuth";
import { useWorkerTimeline } from "../hooks/useWorkerTimeline";
import { useSessionsOpen } from "../hooks/useSessionsOpen";
import { useRecordAttendance } from "../hooks/useAttendance";
import { useStartActivity, useEndActivity } from "../hooks/useActivities";
import { useStartBreak, useEndBreak } from "../hooks/useBreaks";
import { useActivityCatalog } from "../hooks/useActivityCatalog";
import { ACTIVITY_VIEW_TABS, useUiStore } from "../state/uiStore";
import { Icons } from "../components/Icons";
import { HomeDashboardCards } from "../components/HomeDashboardCards";
import { ScreenHeader } from "../components/ScreenHeader";
import { Tabs } from "../components/Tabs";
import {
  codeLabel,
  formatDateFull,
  formatDuration,
  formatTimeFromMs,
  pad,
  relativeDayLabel,
} from "../lib/format";
import {
  buildEvent,
  deriveSessionState,
  getWorkerUrl,
  readContextRefs,
  reconcileSessionState,
} from "../lib/session";
import { slotMaxEndMin } from "../lib/timelineSlots";
import { EditActivityNoteSheet } from "./EditActivityNoteSheet";
import { NewTaskSheet } from "./TasksScreen";
import { TimelineDayView } from "../components/TimelineDayView";
import { TimelineEventList } from "../components/TimelineEventList";
import { SlotEditorSheet } from "./SlotEditorSheet";
import type { SlotDraft } from "./SlotEditorSheet";
import type { EventType, TimelineEvent } from "../api/types";

interface ActiveActivity {
  code: string;
  label: string;
  start: number;
  startEventId: string;
  note: string;
  projects: { id: string; label?: string }[];
  tasks: { id: string; label?: string }[];
  lteTasks: { id: string; label?: string }[];
}

function isoDateStr(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

interface RippleState {
  x: number;
  y: number;
  id: number;
}

export default function HomeScreen() {
  const { user } = useAuth();
  const workerUrl = (() => {
    try {
      return getWorkerUrl(user);
    } catch {
      return "";
    }
  })();

  const online = useUiStore((s) => s.online);
  const pushToast = useUiStore((s) => s.pushToast);
  const openSheet = useUiStore((s) => s.openSheet);
  const openNoteEdit = useUiStore((s) => s.openNoteEdit);
  const actView = useUiStore((s) => s.homeViewMode);
  const setActView = useUiStore((s) => s.setHomeViewMode);

  const catalog = useActivityCatalog();
  const fullCatalog = catalog.data?.activities ?? [];

  const timeline = useWorkerTimeline(
    { worker_url: workerUrl, limit: 200 },
    { enabled: !!workerUrl },
  );
  const sessionsOpen = useSessionsOpen();

  const recordAttendance = useRecordAttendance();
  const startActivity = useStartActivity();
  const endActivity = useEndActivity();
  const startBreak = useStartBreak();
  const endBreak = useEndBreak();

  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [now, setNow] = useState(Date.now());
  const [ripple, setRipple] = useState<RippleState | null>(null);
  const [fabOpen, setFabOpen] = useState(false);
  const [creatingTask, setCreatingTask] = useState(false);
  const [slotDraft, setSlotDraft] = useState<SlotDraft | null>(null);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const allEvents: TimelineEvent[] = useMemo(
    () => timeline.data?.events ?? [],
    [timeline.data?.events],
  );

  const myOpenSession = useMemo(
    () =>
      (sessionsOpen.data?.sessions ?? []).find(
        (s) => s.worker_url === workerUrl,
      ),
    [sessionsOpen.data?.sessions, workerUrl],
  );
  const {
    attendanceOpen,
    attendanceStart,
    activeActivity,
    onBreak,
    breakStart,
  } = reconcileSessionState(deriveSessionState(allEvents), myOpenSession);

  const elapsedMs =
    attendanceOpen && attendanceStart ? now - attendanceStart : 0;
  const elapsed = formatDuration(elapsedMs);

  // Rich active-activity (label + note + event id) for the day banner.
  const lastActivityStart = useMemo(
    () =>
      [...allEvents]
        .filter((e) => e.event_type === "activity_start")
        .sort(
          (a, b) =>
            new Date(b.occurred_at).getTime() -
            new Date(a.occurred_at).getTime(),
        )[0],
    [allEvents],
  );

  const activeActivityRich: ActiveActivity | null =
    activeActivity && lastActivityStart
      ? {
          code: lastActivityStart.activity_code ?? activeActivity.code,
          label:
            fullCatalog.find((c) => c.code === lastActivityStart.activity_code)
              ?.label ??
            lastActivityStart.activity_code ??
            activeActivity.code,
          start: new Date(lastActivityStart.occurred_at).getTime(),
          startEventId: lastActivityStart.id,
          ...(() => {
            const r = readContextRefs(lastActivityStart.context_refs);
            return {
              note: r.note ?? "",
              projects: r.projects,
              tasks: r.tasks,
              lteTasks: r.lteTasks,
            };
          })(),
        }
      : null;

  const selectedDateStr = isoDateStr(selectedDate);
  const dayEvents = allEvents.filter(
    (e) => isoDateStr(new Date(e.occurred_at)) === selectedDateStr,
  );
  const isToday = selectedDateStr === isoDateStr(new Date());
  // Borne du créneau timeline : jamais dans le futur ni sur l'activité en
  // cours : un activity_end postérieur au start live casserait l'état dérivé.
  const slotLimit = slotMaxEndMin(
    selectedDate,
    now,
    activeActivity?.start ?? null,
  );

  function prevDay() {
    setSelectedDate((d) => {
      const n = new Date(d);
      n.setDate(n.getDate() - 1);
      return n;
    });
  }

  function nextDay() {
    setSelectedDate((d) => {
      const n = new Date(d);
      n.setDate(n.getDate() + 1);
      return n;
    });
  }

  const doBadge = async () => {
    if (!workerUrl) return;
    const eventType: EventType = attendanceOpen
      ? "attendance_out"
      : "attendance_in";
    try {
      const payload = await buildEvent(workerUrl, eventType);
      await recordAttendance.mutateAsync(payload);
      pushToast({
        variant: eventType === "attendance_out" ? "out" : "in",
        title:
          eventType === "attendance_out"
            ? "Sortie enregistrée"
            : "Entrée enregistrée",
        subtitle: online
          ? "Synchronisé"
          : "Stocké localement · sera synchronisé",
      });
    } catch {
      pushToast({ variant: "error", title: "Erreur, réessayez" });
    }
  };

  const handleBadge = async (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!workerUrl) return;
    if (navigator.vibrate) navigator.vibrate(30);
    const rect = e.currentTarget.getBoundingClientRect();
    setRipple({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
      id: Date.now(),
    });
    setTimeout(() => setRipple(null), 700);
    await doBadge();
  };

  const handleBreak = async () => {
    if (!workerUrl) return;
    if (navigator.vibrate) navigator.vibrate(20);
    const eventType: EventType = onBreak ? "break_end" : "break_start";
    try {
      const payload = await buildEvent(workerUrl, eventType);
      if (onBreak) {
        await endBreak.mutateAsync(payload);
        pushToast({
          variant: "in",
          title: "Pause terminée",
          subtitle: breakStart
            ? `Durée · ${formatDuration(now - breakStart).short}`
            : undefined,
        });
      } else {
        await startBreak.mutateAsync(payload);
        pushToast({
          variant: "pause",
          title: "Pause démarrée",
          subtitle: "Appuyez à nouveau pour reprendre",
        });
      }
    } catch {
      pushToast({ variant: "error", title: "Erreur, réessayez" });
    }
  };

  const handleEndActivity = async (label?: string) => {
    if (!workerUrl || !activeActivity) return;
    try {
      const payload = await buildEvent(
        workerUrl,
        "activity_end",
        activeActivity.code,
      );
      await endActivity.mutateAsync(payload);
      pushToast({
        variant: "info",
        title: "Activité terminée",
        subtitle: label,
      });
    } catch {
      pushToast({ variant: "error", title: "Erreur, réessayez" });
    }
  };

  const isMutating =
    recordAttendance.isPending ||
    startBreak.isPending ||
    endBreak.isPending ||
    startActivity.isPending ||
    endActivity.isPending;

  return (
    <>
      <ScreenHeader title="Badgeuse" />
      <div className="badge-stage">
        <button
          className={`badge-btn ${attendanceOpen ? "out" : "in"}`}
          onClick={(e) => void handleBadge(e)}
          disabled={isMutating || !workerUrl}
        >
          <div className="icon-wrap">
            {attendanceOpen ? (
              <Icons.arrowOut size={18} sw={2.2} />
            ) : (
              <Icons.arrowIn size={18} sw={2.2} />
            )}
          </div>
          <div className="label">
            {attendanceOpen ? "Pointer sortie" : "Pointer entrée"}
          </div>
          {ripple && (
            <span
              className="ripple"
              key={ripple.id}
              style={{ left: ripple.x, top: ripple.y }}
            />
          )}
        </button>
      </div>

      <div className="actions-row">
        <button
          className={`sec-btn ${activeActivity ? "active" : ""}`}
          disabled={onBreak || isMutating}
          onClick={() => {
            if (activeActivity) {
              void handleEndActivity();
            } else {
              openSheet();
            }
          }}
        >
          <div className="ic">
            {activeActivity ? (
              <Icons.stop size={16} />
            ) : (
              <Icons.play size={16} />
            )}
          </div>
          <div className="text">
            <span className="lbl">
              {activeActivity ? activeActivity.code : "Démarrer activité"}
            </span>
            <span className="sub">
              {activeActivity
                ? `Démarrée à ${formatTimeFromMs(activeActivity.start)}`
                : "Projet, OF, parcelle…"}
            </span>
          </div>
        </button>

        <button
          className={`sec-btn ${onBreak ? "active warn" : ""}`}
          disabled={!attendanceOpen || isMutating}
          onClick={() => void handleBreak()}
        >
          <div className="ic">
            {onBreak ? <Icons.play size={14} /> : <Icons.coffee size={18} />}
          </div>
          <div className="text">
            <span className="lbl">{onBreak ? "Reprendre" : "Pause"}</span>
            <span className="sub">
              {onBreak && breakStart
                ? `En pause · ${formatDuration(now - breakStart).short}`
                : "Démarrer une pause"}
            </span>
          </div>
        </button>
      </div>

      {attendanceOpen && attendanceStart != null && (
        <div className="card session-card" style={{ marginTop: 14 }}>
          <div className="top">
            <div className="label">
              <span className="live-dot" />
              {onBreak ? "En pause" : "Session en cours"}
            </div>
          </div>
          <div
            className="timer-row"
            style={{ display: "flex", alignItems: "baseline", gap: 10 }}
          >
            <div className="timer">{elapsed.hms}</div>
          </div>
          <div className="meta">
            <div className="item">
              <div className="k">Entrée</div>
              <div className="v">{formatTimeFromMs(attendanceStart)}</div>
            </div>
            <div className="item">
              <div className="k">Statut</div>
              <div className="v text">{onBreak ? "En pause" : "Actif"}</div>
            </div>
            {activeActivity && (
              <div className="item">
                <div className="k">Activité</div>
                <div className="v text">{activeActivity.code}</div>
              </div>
            )}
            {!!activeActivity?.projects.length && (
              <div className="item">
                <div className="k">Projet</div>
                <div className="v">
                  {activeActivity.projects.map((p) => (
                    <span key={p.id} className="chip chip-pm">
                      {p.label}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {!!activeActivity?.lteTasks.length && (
              <div className="item">
                <div className="k">Terrain</div>
                <div className="v">
                  {activeActivity.lteTasks.map((t) => (
                    <span key={t.id} className="chip chip-lte">
                      {t.label}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {!!activeActivity?.tasks.length && (
              <div className="item">
                <div className="k">Tâche</div>
                <div className="v">
                  {activeActivity.tasks.map((t) => (
                    <span key={t.id} className="chip chip-tm">
                      {t.label}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
          {activeActivity && !onBreak && (
            <div className="activity-line">
              <span className="dot" />
              <span style={{ flex: 1 }}>
                <strong style={{ fontWeight: 600 }}>
                  {activeActivity.code}
                </strong>
              </span>
              <span className="mono" style={{ fontWeight: 600 }}>
                {formatDuration(now - activeActivity.start).short}
              </span>
            </div>
          )}
          {onBreak && breakStart != null && (
            <div className="activity-line break">
              <span className="dot" />
              <span style={{ flex: 1 }}>
                <strong style={{ fontWeight: 600 }}>Pause</strong> : activité en
                attente
              </span>
              <span className="mono" style={{ fontWeight: 600 }}>
                {formatDuration(now - breakStart).hms}
              </span>
            </div>
          )}
        </div>
      )}

      <HomeDashboardCards />

      <div className="day-header" style={{ marginTop: 18 }}>
        <h2 className="date">Activités</h2>
        <Tabs
          label="Vue des activités"
          value={actView}
          onChange={setActView}
          items={ACTIVITY_VIEW_TABS}
        />
      </div>

      <div className="period-nav">
        <button className="arrow" onClick={prevDay} aria-label="Jour précédent">
          <Icons.chevL size={16} sw={2} />
        </button>
        <div className="label">
          <div className="wk">
            {formatDateFull(selectedDate)}
            {relativeDayLabel(selectedDate) && (
              <span className="rel"> ({relativeDayLabel(selectedDate)})</span>
            )}
          </div>
        </div>
        <button
          className="arrow"
          onClick={nextDay}
          disabled={isToday}
          style={{ opacity: isToday ? 0.4 : 1 }}
          aria-label="Jour suivant"
        >
          <Icons.chev size={16} sw={2} />
        </button>
      </div>

      {activeActivityRich && (
        <div className="active-banner">
          <div className="left">
            <div className="lbl">En cours</div>
            <div className="ttl">
              {codeLabel(activeActivityRich.code, activeActivityRich.label)}
            </div>
            {(activeActivityRich.projects.length > 0 ||
              activeActivityRich.lteTasks.length > 0 ||
              activeActivityRich.tasks.length > 0) && (
              <div className="banner-chips">
                {activeActivityRich.projects.map((p) => (
                  <span key={p.id} className="chip chip-pm">
                    {p.label}
                  </span>
                ))}
                {activeActivityRich.lteTasks.map((t) => (
                  <span key={t.id} className="chip chip-lte">
                    {t.label}
                  </span>
                ))}
                {activeActivityRich.tasks.map((t) => (
                  <span key={t.id} className="chip chip-tm">
                    {t.label}
                  </span>
                ))}
              </div>
            )}
            <button
              type="button"
              onClick={() =>
                openNoteEdit({
                  eventId: activeActivityRich.startEventId,
                  label: codeLabel(
                    activeActivityRich.code,
                    activeActivityRich.label,
                  ),
                  current: activeActivityRich.note,
                })
              }
              className={`note-edit${activeActivityRich.note ? "" : " placeholder"}`}
            >
              <Icons.edit size={12} sw={2} />
              {activeActivityRich.note || "Ajouter une note"}
            </button>
          </div>
          <div className="timer-big">
            {formatDuration(now - activeActivityRich.start).hms}
          </div>
          <button
            className="stop"
            onClick={() => void handleEndActivity(activeActivityRich.label)}
            disabled={endActivity.isPending}
          >
            <Icons.stop size={14} />
          </button>
        </div>
      )}

      {actView === "timeline" ? (
        <TimelineDayView
          dayEvents={dayEvents}
          isToday={isToday}
          onCreateSlot={(startMin, endMin) =>
            setSlotDraft({ startMin, endMin })
          }
        />
      ) : (
        <TimelineEventList
          events={dayEvents}
          onEditNote={(ev) =>
            openNoteEdit({
              eventId: ev.id,
              label: `Activité démarrée${
                ev.activity_code ? ` : ${ev.activity_code}` : ""
              }`,
              current: ev.context_refs?.note
                ? String(ev.context_refs.note)
                : "",
            })
          }
        />
      )}

      <SlotEditorSheet
        draft={slotDraft}
        day={selectedDate}
        catalog={fullCatalog}
        maxEndMin={slotLimit}
        onClose={() => setSlotDraft(null)}
      />

      <EditActivityNoteSheet />

      {creatingTask && <NewTaskSheet onClose={() => setCreatingTask(false)} />}

      <div className="fab-stack">
        {fabOpen && (
          <div className="fab-actions">
            <button
              type="button"
              className="fab-action"
              onClick={() => {
                setFabOpen(false);
                setCreatingTask(true);
              }}
            >
              <Icons.list size={15} sw={2} />
              Nouvelle tâche
            </button>
            <button
              type="button"
              className="fab-action"
              onClick={() => {
                setFabOpen(false);
                openSheet();
              }}
            >
              <Icons.play size={15} sw={2} />
              Démarrer une activité
            </button>
            <button
              type="button"
              className="fab-action"
              onClick={() => {
                setFabOpen(false);
                void doBadge();
              }}
            >
              {attendanceOpen ? (
                <Icons.arrowOut size={15} sw={2} />
              ) : (
                <Icons.arrowIn size={15} sw={2} />
              )}
              {attendanceOpen ? "Badger la sortie" : "Badger l'entrée"}
            </button>
          </div>
        )}
        <button
          type="button"
          className={`fab fab-main${fabOpen ? " open" : ""}`}
          onClick={() => setFabOpen((o) => !o)}
          aria-label="Actions rapides"
          aria-expanded={fabOpen}
        >
          <Icons.plus size={20} sw={2.2} />
        </button>
      </div>
    </>
  );
}
